import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const FootTraffic = () => {
  // State variables for Foot Traffic calculation
  const [totalVisitors, setTotalVisitors] = useState(0); // Total number of visitors
  const [operatingHours, setOperatingHours] = useState(0); // Total operating hours
  const [visitorUnit, setVisitorUnit] = useState("people"); // Unit for visitors
  const [hoursUnit, setHoursUnit] = useState("hours"); // Unit for operating hours
  const [footTraffic, setFootTraffic] = useState(0); // Calculated foot traffic

  // Event handlers for input changes
  const handleTotalVisitorsChange = (e) =>
    setTotalVisitors(parseFloat(e.target.value) || 0);
  const handleOperatingHoursChange = (e) =>
    setOperatingHours(parseFloat(e.target.value) || 0);
  const handleVisitorUnitChange = (e) => setVisitorUnit(e.target.value);
  const handleHoursUnitChange = (e) => setHoursUnit(e.target.value);

  // useEffect hook to calculate Foot Traffic
  useEffect(() => {
    if (operatingHours > 0) {
      const traffic = totalVisitors / operatingHours;
      setFootTraffic(traffic);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [totalVisitors, operatingHours]); // Recalculates when total visitors or operating hours change

  return (
    <Layout title="Foot Traffic Calculator" path="foot-traffic">
      <section className="section-padding" id="foot-traffic">
        <div className="container mt-5">
          <div className="row">
            {/* Foot Traffic Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Foot Traffic Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Total Visitors */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalVisitors" className="form-label">
                        Total Visitors
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Total Visitors:</strong> The number of individuals, families, or groups that visit the area during a given time period."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalVisitors"
                        placeholder="Enter total visitors"
                        value={totalVisitors || ""}
                        onChange={handleTotalVisitorsChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={visitorUnit}
                        onChange={handleVisitorUnitChange}
                      >
                        <option value="people">People</option>
                        <option value="families">Families</option>
                        <option value="groups">Groups</option>
                      </select>
                    </div>

                    {/* Input for Operating Hours */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="operatingHours" className="form-label">
                        Operating Hours
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Operating Hours:</strong> The number of hours (or days/weeks) that the facility is open during the time period considered for foot traffic calculation."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="operatingHours"
                        placeholder="Enter operating hours"
                        value={operatingHours || ""}
                        onChange={handleOperatingHoursChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={hoursUnit}
                        onChange={handleHoursUnitChange}
                      >
                        <option value="hours">Hours</option>
                        <option value="days">Days</option>
                        <option value="weeks">Weeks</option>
                      </select>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Foot Traffic:{" "}
                        <span className="fw-bold ms-3">
                          {footTraffic === 0
                            ? "0"
                            : footTraffic.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          visitors per {hoursUnit}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Foot Traffic Calculation
                </h3>

                {/* What is Foot Traffic? */}
                <h4 className="mt-3 mb-3">What is Foot Traffic?</h4>
                <p>
                  **Foot Traffic** refers to the number of visitors entering a
                  specific location, such as a shopping mall or retail space,
                  over a given time period. It is a key metric for understanding
                  customer behavior and space utilization.
                </p>

                {/* Why Use the Foot Traffic Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use the Foot Traffic Calculator?
                </h4>
                <p>
                  This calculator helps in estimating the average number of
                  visitors during specific operating hours, enabling better
                  management of resources and optimizing operations.
                </p>

                {/* Benefits of Using the Foot Traffic Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using the Foot Traffic Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Improves Resource Allocation:</strong> Helps plan
                    staffing and resource usage effectively.
                  </li>
                  <li>
                    <strong>Supports Marketing Strategies:</strong> Provides
                    insights into peak traffic times for targeted promotions.
                  </li>
                  <li>
                    <strong>Enhances Retail Space Planning:</strong> Assists in
                    optimizing layout and services based on visitor patterns.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use the Foot Traffic Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Shopping Malls:</strong> To monitor daily and
                    seasonal visitor patterns.
                  </li>
                  <li>
                    <strong>Retail Management:</strong> For planning store
                    layouts and scheduling staff.
                  </li>
                  <li>
                    <strong>Property Management:</strong> To analyze visitor
                    flow for operational decisions.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h5 className="mt-3 mb-2">
                  How can foot traffic data help with marketing strategies?
                </h5>
                <p>
                  Understanding foot traffic patterns allows businesses to time
                  their promotions during peak hours, target specific audience
                  groups, and improve customer engagement.
                </p>

                <h5 className="mt-3 mb-2">
                  Is this calculator applicable to venues other than shopping
                  malls?
                </h5>
                <p>
                  Yes, this calculator can be used for various venues such as
                  airports, events, museums, and any location where visitor flow
                  needs to be tracked.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default FootTraffic;
